<template>
  <div class="cuadrante-classic">
    <!-- Controls Header -->
    <div class="cuadrante-controls no-print mb-4 pa-4 mdtec-card elevation-1">
      <v-row align="center" no-gutters>
        <!-- Selectors Group -->
        <v-col cols="auto" class="d-flex align-center mr-6">
          <label class="mr-3 font-weight-bold mb-0">Año:</label>
          <v-select v-model="selectedYear" :items="years" outlined dense hide-details style="width: 110px"
            @change="loadCuadrante"></v-select>
        </v-col>

        <v-col cols="auto" class="d-flex align-center mr-6">
          <label class="mr-3 font-weight-bold mb-0">Mes:</label>
          <v-select v-model="selectedMonth" :items="months" item-text="name" item-value="value" outlined dense
            hide-details style="width: 160px" @change="loadCuadrante"></v-select>
        </v-col>

        <v-spacer></v-spacer>

        <!-- Actions Group -->
        <v-col cols="auto" class="d-flex">
          <v-btn color="primary" outlined class="mr-2" @click="loadCuadrante" :loading="loading">
            <v-icon left>mdi-refresh</v-icon>
            Refrescar
          </v-btn>
          <!-- Actions Imprimir / PDF
          <v-btn color="secondary" @click="printCuadrante" :disabled="loading || !workersData.length">
            <v-icon left>mdi-printer</v-icon>
            Imprimir / PDF
          </v-btn>
          -->
        </v-col>
      </v-row>
    </div>

    <!-- Print Header (Visible only when printing) -->
    <div class="print-only-header mb-4">
      <div class="d-flex justify-space-between align-end">
        <div>
          <h1 class="text-h4 mb-1">CUADRANTE DE SERVICIO</h1>
          <h2 class="text-h6 text--secondary">{{ workcenterName }}</h2>
        </div>
        <div class="text-right">
          <div class="text-h5 font-weight-bold">{{ selectedMonthName }} {{ selectedYear }}</div>
        </div>
      </div>
      <v-divider class="my-4"></v-divider>
    </div>
    <!-- Legend -->
    <div class="cuadrante-legend pa-4 mt-6 grey lighten-4 rounded-lg elevation-1 no-print my-2">
      <h3 class="text-subtitle-1 font-weight-bold mb-3">Leyenda de Turnos:</h3>
      <div class="d-flex flex-wrap">
        <div class="legend-item mr-6 mb-2">
          <span class="legend-box shift-m">M</span>
          <span>Mañana</span>
        </div>
        <div class="legend-item mr-6 mb-2">
          <span class="legend-box shift-t">T</span>
          <span>Tarde</span>
        </div>
        <div class="legend-item mr-6 mb-2">
          <span class="legend-box shift-n">N</span>
          <span>Noche</span>
        </div>
        <div class="legend-item mr-6 mb-2">
          <span class="legend-box shift-l">L</span>
          <span>Libre / Descanso</span>
        </div>
        <div class="legend-item mr-6 mb-2">
          <span class="legend-box shift-a">A</span>
          <span>Absentismo / Baja</span>
        </div>
        <div class="legend-item mr-6 mb-2">
          <span class="legend-box shift-v">V</span>
          <span>Vacaciones</span>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="d-flex justify-center align-center py-12">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <span class="ml-4 text-h6 text--secondary">Cargando cuadrante...</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="!workersData.length" class="text-center py-12">
      <v-icon size="80" color="grey lighten-1">mdi-calendar-blank</v-icon>
      <p class="text-h6 grey--text text--darken-1 mt-4">No se han encontrado datos para el periodo seleccionado.</p>
    </div>

    <!-- Cuadrante Table -->
    <div v-else class="cuadrante-container elevation-2 mb-6">
      <div class="table-scroll-wrapper">
        <table class="cuadrante-table">
          <thead>
            <!-- Days Numbers Row -->
            <tr>
              <th class="sticky-column worker-name-header">Trabajador/a</th>
              <th v-for="day in daysInMonth" :key="'num-' + day" class="day-cell"
                :class="{ 'weekend-cell': isWeekend(day) }">
                {{ String(day).padStart(2, '0') }}
              </th>
            </tr>
            <!-- Weekdays Row -->
            <tr class="weekdays-row">
              <th class="sticky-column"></th>
              <th v-for="day in daysInMonth" :key="'wd-' + day" class="day-cell-wd"
                :class="{ 'weekend-cell': isWeekend(day) }">
                {{ getWeekdayInitial(day) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="worker in workersData" :key="worker.id">
              <td class="sticky-column worker-name">
                {{ worker.fullName }}
              </td>
              <td v-for="day in daysInMonth" :key="'cell-' + worker.id + '-' + day" class="shift-cell"
                :class="getShiftClass(worker.id, day)">
                {{ getShiftInitial(worker.id, day) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

  </div>
</template>

<script>
import workcenterService from '@/services/workcenter.service';
import scheduleService from '@/services/schedule.service';

export default {
  name: 'CuadranteClassic',
  props: {
    workcenterId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    const now = new Date();
    return {
      loading: false,
      selectedYear: now.getFullYear(),
      selectedMonth: now.getMonth() + 1, // 1-12
      years: [2022, 2023, 2024, 2025, 2026],
      months: [
        { name: 'Enero', value: 1 },
        { name: 'Febrero', value: 2 },
        { name: 'Marzo', value: 3 },
        { name: 'Abril', value: 4 },
        { name: 'Mayo', value: 5 },
        { name: 'Junio', value: 6 },
        { name: 'Julio', value: 7 },
        { name: 'Agosto', value: 8 },
        { name: 'Septiembre', value: 9 },
        { name: 'Octubre', value: 10 },
        { name: 'Noviembre', value: 11 },
        { name: 'Diciembre', value: 12 },
      ],
      workersData: [],
      schedulesMap: {}, // workerId -> { dayNumber -> shiftType }
      workcenterName: '',
    };
  },
  computed: {
    daysInMonth() {
      const days = new Date(this.selectedYear, this.selectedMonth, 0).getDate();
      return Array.from({ length: days }, (_, i) => i + 1);
    },
    selectedMonthName() {
      const monthObj = this.months.find((m) => m.value === this.selectedMonth);
      return monthObj ? monthObj.name : '';
    },
  },
  mounted() {
    this.getWorkcenterInfo();
    this.loadCuadrante();
  },
  methods: {
    async getWorkcenterInfo() {
      try {
        const wc = await workcenterService.getWorkcenter(this.workcenterId);
        this.workcenterName = wc.name;
      } catch (e) {
        console.error('Error fetching workcenter info', e);
      }
    },
    async loadCuadrante() {
      if (!this.workcenterId) return;
      this.loading = true;
      this.workersData = [];
      this.schedulesMap = {};

      try {
        // 1. Get workers
        const workersRes = await workcenterService.getWorkers({
          workCenter: this.workcenterId,
          size: 999,
          sort: 'name',
          direction: 'asc',
        });
        const workers = workersRes.content || [];
        this.workersData = workers.map((w) => ({
          id: w.id,
          fullName: `${w.name} ${w.lastName1 || ''}`,
        }));

        if (workers.length === 0) {
          this.loading = false;
          return;
        }

        // 2. Fetch schedules for all workers in parallel
        const startDate = `${this.selectedYear}-${String(this.selectedMonth).padStart(2, '0')}-01`;
        const endDate = `${this.selectedYear}-${String(this.selectedMonth).padStart(2, '0')}-${this.daysInMonth.length}`;

        const promises = workers.map((worker) => scheduleService.getDaysInRange(worker.id, startDate, endDate));

        const results = await Promise.all(promises);

        // 3. Map results
        results.forEach((workerDays, index) => {
          const workerId = workers[index].id;
          const dayMap = {};

          if (workerDays && Array.isArray(workerDays)) {
            workerDays.forEach((dayEntry) => {
              const dayNum = new Date(dayEntry.date).getDate();
              dayMap[dayNum] = dayEntry.type;
            });
          }
          this.$set(this.schedulesMap, workerId, dayMap);
        });
      } catch (e) {
        console.error('Error loading cuadrante data', e);
      } finally {
        this.loading = false;
      }
    },
    getShiftInitial(workerId, day) {
      const type = this.schedulesMap[workerId] ? this.schedulesMap[workerId][day] : null;
      if (!type) return '';

      switch (type) {
        case 'MORNING': return 'M';
        case 'EVENING': return 'T';
        case 'NIGHT': return 'N';
        case 'ABSENCE': return 'A';
        case 'VACATIONS': return 'V';
        case 'LEAVE': return 'L';
        default: return '';
      }
    },
    getShiftClass(workerId, day) {
      const type = this.schedulesMap[workerId] ? this.schedulesMap[workerId][day] : null;
      if (!type) return '';

      switch (type) {
        case 'MORNING': return 'shift-m';
        case 'EVENING': return 'shift-t';
        case 'NIGHT': return 'shift-n';
        case 'ABSENCE': return 'shift-a';
        case 'VACATIONS': return 'shift-v';
        case 'LEAVE': return 'shift-l';
        default: return '';
      }
    },
    getWeekdayInitial(day) {
      const date = new Date(this.selectedYear, this.selectedMonth - 1, day);
      const days = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
      return days[date.getDay()];
    },
    isWeekend(day) {
      const date = new Date(this.selectedYear, this.selectedMonth - 1, day);
      const dayOfWeek = date.getDay();
      return dayOfWeek === 0 || dayOfWeek === 6; // 0=Sunday, 6=Saturday
    },
    printCuadrante() {
      window.print();
    },
  },
};
</script>

<style lang="scss" scoped>
.cuadrante-classic {
  width: 100%;
  font-family: "Roboto", sans-serif;
}

.table-scroll-wrapper {
  overflow-x: auto;
  max-width: 100%;
  background-color: white;

  .theme--dark & {
    background-color: #1e1e1e;
  }
}

.cuadrante-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  table-layout: fixed;

  th,
  td {
    border: 1px solid #e0e0e0;
    text-align: center;
    padding: 8px 4px;
    height: 40px;

    .theme--dark & {
      border-color: #444;
    }
  }

  thead {
    background-color: #f5f5f5;
    position: sticky;
    top: 0;
    z-index: 2;

    .theme--dark & {
      background-color: #2c2c2c;
    }

    th {
      font-weight: 700;
      color: #333;

      .theme--dark & {
        color: #fff;
      }
    }
  }

  .sticky-column {
    position: sticky;
    left: 0;
    background: #fdfdfd;
    z-index: 3;
    width: 200px;
    min-width: 200px;
    text-align: left;
    padding-left: 12px;
    border-right: 2px solid #bdbdbd;
    box-shadow: 2px 0 5px rgba(0, 0, 0, 0.05);

    .theme--dark & {
      background: #1e1e1e;
      border-right-color: #555;
    }
  }

  .worker-name-header {
    background: #eeeeee;

    .theme--dark & {
      background: #333;
    }
  }

  .worker-name {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .day-cell {
    width: 32px;
    min-width: 32px;
  }

  .day-cell-wd {
    font-size: 0.75rem;
    color: #666;

    .theme--dark & {
      color: #aaa;
    }
  }

  .weekend-cell {
    background-color: #f0f0f0 !important;
    color: #ef5350 !important;

    .theme--dark & {
      background-color: #3d3d3d !important;
      color: #ff8a80 !important;
    }
  }

  .shift-cell {
    font-weight: 800;
    font-size: 1rem;
    transition: all 0.2s;

    &:hover {
      filter: brightness(0.9);
    }
  }
}

// Shift Colors
.shift-m {
  background-color: #b3e5fc;
  color: #0277bd;
}

// Morning (Light Blue)
.shift-t {
  background-color: #fff9c4;
  color: #f9a825;
}

// Evening (Yellow)
.shift-n {
  background-color: #f8bbd0;
  color: #c2185b;
}

// Night (Pink)
.shift-l {
  background-color: #ffffff;
  color: #9e9e9e;

  .theme--dark & {
    background-color: #333333;
    color: #bbbbbb;
  }
}

// Leave (White/Grey)
.shift-a {
  background-color: #c8e6c9;
  color: #2e7d32;
}

// Absence (Green)
.shift-v {
  background-color: #e1bee7;
  color: #7b1fa2;
}

// Vacation (Purple)

.legend-box {
  display: inline-block;
  width: 24px;
  height: 24px;
  line-height: 24px;
  text-align: center;
  border-radius: 4px;
  font-weight: bold;
  margin-right: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  font-size: 0.75rem;
}

.cuadrante-legend {
  &.grey.lighten-4 {
    .theme--dark & {
      background-color: #2c2c2c !important;
      color: #eee !important;
    }
  }
}

.print-only-header {
  display: none;
}

@media print {
  .no-print {
    display: none !important;
  }

  .print-only-header {
    display: block !important;
  }

  .cuadrante-classic {
    padding: 0 !important;
  }

  .cuadrante-container {
    box-shadow: none !important;
  }

  .cuadrante-table {
    font-size: 0.7rem;

    th,
    td {
      padding: 4px 2px;
      height: 25px;
      border: 1px solid #333 !important;
    }

    .sticky-column {
      position: static !important;
      width: 150px !important;
      min-width: 150px !important;
      border-right: 1px solid #333 !important;
      box-shadow: none !important;
    }

    .shift-m {
      background-color: #b3e5fc !important;
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }

    .shift-t {
      background-color: #fff9c4 !important;
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }

    .shift-n {
      background-color: #f8bbd0 !important;
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }

    .shift-a {
      background-color: #c8e6c9 !important;
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }

    .shift-v {
      background-color: #e1bee7 !important;
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }
  }

  @page {
    size: landscape;
    margin: 1cm;
  }
}
</style>
